import { useState } from "react";
import { Flame, ImageIcon, ImageOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import type { PosterTemplateBrief, PosterTemplateRatio } from "../../../api/posterTemplateService";
import { USE_CASES } from "../../../longimage/model/blocks";
import { TemplateSkeletonPreview, skeletonBands } from "./TemplateSkeletonPreview";

const RATIO_CLASS: Record<PosterTemplateRatio, string> = {
  "9:16": "aspect-[9/16]",
  "1:1": "aspect-square",
  "4:5": "aspect-[4/5]",
  "16:9": "aspect-video",
  "3:4": "aspect-[3/4]",
};

interface Props {
  tpl: PosterTemplateBrief;
  /** Visual size: `card` (gallery card) or `hero` (large preview in sheet/page). */
  size?: "card" | "hero";
  className?: string;
}

/** V2 (2026-05-11) — IMAGE-only: 直接显示 cover_image_url + provider/ratio 角标。
 *
 * V8.1 (2026-05-12) — cover 加载失败(URL 404 / MinIO 签名过期 / 网络挂)时
 * 显示 fallback 占位 + "封面缺失" 灰字,避免浏览器默认 broken image 跟
 * 父 div bg-muted/30 暗色背景叠在一起出现"一片黑"。
 *
 * V8.5 (2026-05-12) — gallery 列表 card 模式优先用 600px thumbUrl,避免每张原图
 * 几 MB 拖慢首屏。hero 模式(套模板大图 / detail)仍用原 coverImageUrl 保清晰度。
 * thumb 加载失败 / 模板还没生成 thumb (老数据) 时 fallback 原 cover。
 */
/** LONGIMAGE 的 scene 列 = 用途 key(P5);查不到就不显(不硬造)。 */
function usageLabelOf(scene: string | undefined): string | null {
  return USE_CASES.find((u) => u.key === scene)?.label ?? null;
}

/** 骨架块数(schemaJson.blocks;读不出就不显)。 */
function blockCountOf(tpl: PosterTemplateBrief): number | null {
  const blocks = (tpl.schemaJson as { blocks?: unknown } | null)?.blocks;
  return Array.isArray(blocks) && blocks.length > 0 ? blocks.length : null;
}

export function TemplatePreview({ tpl, size = "card", className }: Props) {
  const { t } = useTranslation("poster");
  const ratioCls = RATIO_CLASS[tpl.ratio] ?? "aspect-[9/16]";
  const hero = size === "hero";
  const [imgFailed, setImgFailed] = useState(false);
  // card 模式 → thumb 优先(快);hero 模式 → 原 cover(清晰)
  const preferredSrc = hero ? tpl.coverImageUrl : tpl.thumbUrl || tpl.coverImageUrl;
  const showImg = !!preferredSrc && !imgFailed;
  /* 骨架卡面只在**读得出骨架**时才画:坏 JSON / 空 blocks 仍旧回落「暂无封面 / 封面缺失」,
     不硬画一个猜出来的形状。(★2026-08-26:原来还要求 type==='LONGIMAGE',type 列删除后
     模板只剩长图一种形态,那半个条件恒真已去掉。) */
  const skeleton = skeletonBands(tpl.schemaJson).length > 0;

  return (
    <div className={cn("relative w-full overflow-hidden bg-muted/30", ratioCls, className)}>
      {showImg ? (
        <img
          src={preferredSrc}
          alt={tpl.name}
          loading="lazy"
          decoding="async"
          onError={() => setImgFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : skeleton ? (
        /* ★没有封面的长图模板:用 schema_json 画出这套模板的**形状**。
           模板存的本来就不是画面而是骨架 —— 贴一张成品封面反而在暗示「套了就长这样」。
           详见 TemplateSkeletonPreview 的文件注释。 */
        <TemplateSkeletonPreview schemaJson={tpl.schemaJson} />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
          {imgFailed ? (
            <>
              <ImageOff className={cn(hero ? "h-12 w-12" : "h-6 w-6")} />
              <span className={cn(hero ? "text-xs" : "text-2xs")}>{t("homeV3.coverFailed")}</span>
            </>
          ) : (
            <>
              <ImageIcon className={cn(hero ? "h-12 w-12" : "h-6 w-6")} />
              <span className={cn(hero ? "text-xs" : "text-2xs")}>{t("homeV3.noCover")}</span>
            </>
          )}
        </div>
      )}

      {/* 角标(★蒙版用 /70 不用 /40,门 H-D 实测 /40 白字 2.92:1 不过 AA):
          显骨架真值:用途 + 区块数。★2026-08-26:原来还有一条 IMAGE 分支显 provider + ratio,
          type 列删除后不可达 —— 而且长图行的这两列本来就是 P5 占位值,显示出来是把占位当真相。 */}
      <div className="absolute right-2 top-2 flex items-center gap-1">
        {usageLabelOf(tpl.scene) && (
          <span
            className={cn(
              "rounded-md border border-white/30 bg-black/70 font-medium text-white backdrop-blur",
              hero ? "px-2 py-1 text-xs" : "px-1.5 py-0.5 text-2xs"
            )}
          >
            {usageLabelOf(tpl.scene)}
          </span>
        )}
        {blockCountOf(tpl) != null && (
          <span
            className={cn(
              "rounded-md border border-white/30 bg-black/70 font-mono font-medium text-white backdrop-blur",
              hero ? "px-2 py-1 text-xs" : "px-1.5 py-0.5 text-2xs"
            )}
          >
            {blockCountOf(tpl)} 块
          </span>
        )}
      </div>

      {/* 热门标记 — V8.1 (2026-05-12) Flame SVG 替代 🔥 emoji,跟 admin 模板列表对齐 */}
      {tpl.isHot && (
        <div className="absolute left-2 top-2">
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full bg-destructive/80 font-medium text-white backdrop-blur",
              hero ? "px-2.5 py-1 text-xs" : "px-1.5 py-0.5 text-2xs"
            )}
          >
            <Flame className={cn(hero ? "h-3.5 w-3.5" : "h-2.5 w-2.5")} />
            {t("homeV3.hot")}
          </span>
        </div>
      )}
    </div>
  );
}
